import BenchlingConfig from 'BenchlingConfig';
import {MAX_TABLE_SIZE as BULK_ASSEMBLY_MAX_TABLE_SIZE} from 'molbio/bulk-assembly/BulkAssemblyConstants';
import {PROCEDURE_PRODUCT_LIMITS} from 'procedures/design/ProcedureDesignConstants';

import {StructuredTableKind} from '../StructuredTableConstants';

/**
 * Get the max number of allowed rows according to config flags and specific table type overrides.
 *
 * For persisted (editable) tables, there are three places a structured table's max length can be set.
 * Listed in order of priority, they are:
 * - The ELN_MAX_STRUCTURED_TABLE_PERSISTED_ROWS_OVERRIDE[tableKind] config
 * - The hard-coded general table kind override
 * - The ELN_MAX_STRUCTURED_TABLE_PERSISTED_ROWS config
 *
 * This should stay in sync with StructuredTable.get_max_row_capacity on the backend.
 *
 * @param isTableEditable optional, default False. Should be specified as True if the table in question is persisted in the database, as opposed to being generated to display data. Most tables in the notebook are considered editable. Examples of NON editable tables include tables in Runs or the Results pane.
 * @param tableKind The table kind. Some tenants specify max row limit overrides for certain table types. If kind is not specified, overrides won't be checked. Overrides are not used for non-editable tables.
 * @returns the max allowed rows for the structured table. If tableIsEditable, this will be ELN_MAX_STRUCTURED_TABLE_PERSISTED_ROWS, or an override specific to the table type; if not, it will be ELN_MAX_STRUCTURED_TABLE_ROWS.
 */
export function getMaxRowsConfig(isTableEditable?: boolean, tableKind?: StructuredTableKind): number {
  if (isTableEditable) {
    // Check if there is an override for the specific table kind
    if (tableKind) {
      const configOverride = BenchlingConfig.ELN_MAX_STRUCTURED_TABLE_PERSISTED_ROWS_OVERRIDE[tableKind];
      const generalOverride = TABLE_MAX_LENGTH_GENERAL_OVERRIDES[tableKind];
      if (configOverride || generalOverride) {
        return configOverride ?? generalOverride;
      }
    }
    return BenchlingConfig.ELN_MAX_STRUCTURED_TABLE_PERSISTED_ROWS;
  }
  return BenchlingConfig.ELN_MAX_STRUCTURED_TABLE_ROWS;
}

export const TABLE_MAX_LENGTH_GENERAL_OVERRIDES: {[key: string]: number} = {
  [StructuredTableKind.PROCEDURE_MATERIAL_INPUTS]: PROCEDURE_PRODUCT_LIMITS.MATERIAL_INPUTS_PER_METHOD,
  [StructuredTableKind.PROCEDURE_MATERIAL_OUTPUTS]: PROCEDURE_PRODUCT_LIMITS.MATERIAL_OUTPUTS_PER_METHOD,
  [StructuredTableKind.PROCEDURE_DATA_PARAMETERS_CONFIG]: PROCEDURE_PRODUCT_LIMITS.DATA_PARAMETERS_PER_METHOD,
  [StructuredTableKind.PROCEDURE_EQUIPMENT]: PROCEDURE_PRODUCT_LIMITS.EQUIPMENT_PER_METHOD,
  [StructuredTableKind.BULK_ASSEMBLY_FRAGMENTS]: BULK_ASSEMBLY_MAX_TABLE_SIZE,
  [StructuredTableKind.BULK_ASSEMBLY_CONSTRUCTS]: BULK_ASSEMBLY_MAX_TABLE_SIZE,
};

/**
 * Get the max number of rows that the table can grow to, reserving room for pending rows added through the API.
 * @param structuredTableKind: The table kind. Some tenants specify max row limit overrides for certain table types. If kind is not specified, overrides won't be checked.
 * @param isTableEditable: Defaults to false. Should be specified as true for tables that are persisted normally in the database, as is the case with most tables in the notebook. Most tables outside the notebook are not considered editable, as they are only generated to display data or facilitate actions and do not stick around. This distinction affects which config flag is used to determine max allowed rows.
 * @param numApiRowsPending: Defaults to zero. This is the number of rows that have been added to the table via the API, but are still in the pending state and have not yet been added to the table in the UI. This number is usually stored on the table.
 * @returns The max number of rows that the table can grow to without exceeding the tenant-configured limit for that table type.
 */
export function getTableLengthCapacity(props: {
  structuredTableKind?: StructuredTableKind;
  isTableEditable?: boolean;
  numApiRowsPending?: number;
}): number {
  const rowLimit = getMaxRowsConfig(props.isTableEditable, props.structuredTableKind);
  return rowLimit - (props.numApiRowsPending ?? 0);
}

/**
 * Get the max allowed rows for the given circumstances, and return an appropriate error message if the newTableLength is too long.
 * @param currentTableLength The number of rows that are already in the table.
 * @param newTableLength The total number of rows that would be in the table if an operation succeeded. Usually more than `currentTableLength`, but may be less if more empty trailing rows were removed than there were new rows added.
 * @param structuredTableKind The table kind. Some tenants specify max row limit overrides for certain table types. If kind is not specified, overrides won't be checked.
 * @param isTableEditable Defaults to false. Should be specified as true for tables that are persisted normally in the database, as is the case with most tables in the notebook. Most tables outside the notebook are not considered editable, as they are only generated to display data or facilitate actions and do not stick around. This distinction affects which config flag is used to determine max allowed rows.
 * @param allowAddRows Defaults to false. Should be specified as true for tables that allow adding rows.
 * @param allowRemoveRows Defaults to false. Should be specified as true for tables that allow deleting rows.
 * @param numApiRowsPending Defaults to zero. This is the number of rows that have been added to the table via the API, but are still in the pending state and have not yet been added to the table in the UI. This number is usually stored on the table.
 * @param numPendingInAdded Defaults to zero. This is the number of rows that are accounted for in both newTableLength and numApiRowsPending, and is need to avoid double counting them when reserving room for pending rows.
 * @returns null if newTableLength is <= max, otherwise returns an informative string that can be used as an error message.
 */
export function getTableLengthErrorMessage(props: {
  currentTableLength: number;
  newTableLength: number;
  structuredTableKind?: StructuredTableKind;
  isTableEditable?: boolean;
  allowAddRows?: boolean;
  allowRemoveRows?: boolean;
  numApiRowsPending?: number;
  numPendingInAdded?: number;
}): string | null {
  const {
    structuredTableKind,
    currentTableLength,
    newTableLength,
    isTableEditable,
    allowAddRows,
    allowRemoveRows,
    numApiRowsPending,
    numPendingInAdded,
  } = props;
  if (
    newTableLength >
    getTableLengthCapacity({structuredTableKind, isTableEditable, numApiRowsPending}) +
      (numPendingInAdded ?? 0)
  ) {
    return `Adding those rows would exceed the allowable size of the table (${getTableLengthString({
      currentTableLength,
      structuredTableKind,
      isTableEditable,
      numApiRowsPending,
    })}).`;
  }
  if (
    (!allowAddRows && currentTableLength < newTableLength) ||
    (!allowRemoveRows && currentTableLength > newTableLength)
  ) {
    return `Cannot change table length. Table is locked at ${currentTableLength} rows; operation would change it to ${newTableLength}.`;
  }
  return null;
}

/**
 * Get a string describing the max rows allowed, with the current count of present and pending rows.
 * @param currentTableLength The current length of the table.
 * @param structuredTableKind The table kind. Some tenants specify max row limit overrides for certain table types. If kind is not specified, overrides won't be checked.
 * @param isTableEditable Defaults to false. Should be specified as true for tables that are persisted normally in the database, as is the case with most tables in the notebook. Most tables outside the notebook are not considered editable, as they are only generated to display data or facilitate actions and do not stick around. This distinction affects which config flag is used to determine max allowed rows.
 * @param numApiRowsPending Defaults to zero. This is the number of rows that have been added to the table via the API, but are still in the pending state and have not yet been added to the table in the UI. This number is usually stored on the table.
 * @returns "N rows max (n present, n pending)", with present and pending counts individually excluded if they are zero.
 */
export function getTableLengthString(props: {
  currentTableLength: number;
  structuredTableKind?: StructuredTableKind;
  isTableEditable?: boolean;
  numApiRowsPending?: number;
}): string {
  const {currentTableLength, structuredTableKind, isTableEditable, numApiRowsPending} = props;
  const configMaxRows = getTableLengthCapacity({structuredTableKind, isTableEditable, numApiRowsPending});
  const pendingRows = numApiRowsPending ?? 0;
  const presentRows = currentTableLength;

  const maxRowsStr = `${configMaxRows} rows max`;
  const currentRowsStr = presentRows > 0 ? `${presentRows} present` : '';
  const pendingRowsStr = pendingRows > 0 ? `${pendingRows} pending` : '';

  if (currentRowsStr.length > 0 && pendingRowsStr.length > 0) {
    return `${maxRowsStr}; ${currentRowsStr}, ${pendingRowsStr}`;
  } else if (currentRowsStr.length > 0) {
    return `${maxRowsStr}; ${currentRowsStr}`;
  } else if (pendingRowsStr.length > 0) {
    return `${maxRowsStr}; ${pendingRowsStr}`;
  }
  return maxRowsStr;
}
